import * as React from 'react'

import { toast } from 'sonner'

type FetchErrorToastContextValue = {
  reportFetchError: (cardTitle: string) => void
  reset: () => void
}

const FetchErrorToastContext = React.createContext<
  FetchErrorToastContextValue | undefined
>(undefined)

const AGGREGATION_WINDOW_MS = 500

export function DutyFetchErrorToastProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const pendingRef = React.useRef<string[]>([])
  const flushedRef = React.useRef(false)
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const flush = React.useCallback(() => {
    timerRef.current = null
    if (pendingRef.current.length === 1) {
      toast.error(`Failed to load ${pendingRef.current[0]}`)
    } else {
      toast.error('Failed to load duty details')
    }
    flushedRef.current = true
  }, [])

  const reportFetchError = React.useCallback(
    (cardTitle: string) => {
      if (flushedRef.current) return

      pendingRef.current = [...pendingRef.current, cardTitle]

      if (pendingRef.current.length >= 2) {
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current)
          timerRef.current = null
        }
        toast.error('Failed to load duty details')
        flushedRef.current = true
        return
      }

      // First error — start the aggregation timer
      timerRef.current = setTimeout(flush, AGGREGATION_WINDOW_MS)
    },
    [flush]
  )

  const reset = React.useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    pendingRef.current = []
    flushedRef.current = false
  }, [])

  React.useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const value = React.useMemo<FetchErrorToastContextValue>(
    () => ({ reportFetchError, reset }),
    [reportFetchError, reset]
  )

  return (
    <FetchErrorToastContext.Provider value={value}>
      {children}
    </FetchErrorToastContext.Provider>
  )
}

const NOOP_VALUE: FetchErrorToastContextValue = {
  reportFetchError: () => {},
  reset: () => {},
}

export function useFetchErrorToast(): FetchErrorToastContextValue {
  return React.useContext(FetchErrorToastContext) ?? NOOP_VALUE
}
